import React, { useEffect, useState, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import {
  getLifecycleRules,
  createLifecycleRule,
  updateLifecycleRule,
  deleteLifecycleRule,
  previewLifecycleRule,
  applyLifecycleRule,
  getLifecycleRuns,
  setRetentionGuard,
  getDatabaseIndexes,
  getStorageLocations,
  getDatabases,
} from "../../api/adminApi";

const AGE_UNITS = ["Days", "Months", "Years"];

// Structural columns the SCOPE step can narrow by (base document-table columns, present on every
// table). A scope = one equality clause AND-ed with the WHERE condition under the hood.
const SCOPE_FIELDS = ["ArchiveNo", "Channel", "Sender", "Status"];
const SCOPE_FIELD_KEY = { ArchiveNo: "scopeArchive", Channel: "scopeChannel", Sender: "scopeSender", Status: "scopeStatus" };

// Built-in document columns offered as WHERE-condition field suggestions (before per-DB index fields).
const SYSTEM_FIELDS = ["Status", "Channel", "Sender", "ArchiveNo", "OriginalFileName", "DocumentDate", "DocumentUUID"];
// Comparison operators the visual WHERE builder exposes (mirrors FilterExpressionToSqlConverter).
const WHERE_OPS = ["$eq", "$neq", "$gt", "$gte", "$lt", "$lte", "$like", "$between"];

const emptyForm = {
  id: 0, name: "", description: "", enabled: true, priority: 0,
  dateBasis: "DocumentDate", maxAgeValue: "", maxAgeUnit: "Years",
  action: "SoftDelete", deletionDepth: "Physical",
  // Move target: "Tier" relocates archive bytes to another storage location (record stays);
  // "Database" re-homes the documents into another DEPOSITORY DB. Only one id is meaningful at a time.
  moveTargetType: "Tier", moveTargetStorageLocationId: "", moveTargetDbId: "",
  executionMode: "Manual", intervalHours: 24,
  scopeField: "", scopeValue: "", conditionJson: "",
};

// Database id/name accessors tolerate the API's camelCase/PascalCase variants (mirrors LifecycleRulesPage).
function dbId(d) { return d?.id ?? d?.Id ?? d?.dbInfoId ?? d?.DBInfoId; }
function dbName(d) { return d?.name ?? d?.Name ?? d?.displayName ?? d?.DisplayName ?? d?.dbName ?? d?.DbName ?? String(dbId(d)); }

// Decompose a Move rule's ActionParamsJson back into the form's move fields (best-effort round-trip).
function splitMoveParams(actionParamsJson) {
  const none = { moveTargetType: "Tier", moveTargetStorageLocationId: "", moveTargetDbId: "" };
  if (!actionParamsJson) return none;
  try {
    const p = JSON.parse(actionParamsJson);
    const type = String(p?.targetType || "").toLowerCase() === "database" ? "Database" : "Tier";
    return {
      moveTargetType: type,
      moveTargetStorageLocationId: type === "Tier" && p?.targetStorageLocationId ? String(p.targetStorageLocationId) : "",
      moveTargetDbId: type === "Database" && p?.targetDbId ? String(p.targetDbId) : "",
    };
  } catch { return none; }
}

// Split a stored ConditionJson back into the structural scope clause + the free-form WHERE so
// editing round-trips. Best-effort: anything that isn't a clean structural equality stays in WHERE.
function splitCondition(condJson) {
  const none = { scopeField: "", scopeValue: "", conditionJson: "" };
  if (!condJson) return none;
  let obj;
  try { obj = JSON.parse(condJson); } catch { return { ...none, conditionJson: condJson }; }
  const asScope = (node) => {
    if (!node || typeof node !== "object" || Array.isArray(node)) return null;
    const keys = Object.keys(node);
    if (keys.length !== 1) return null;
    const col = keys[0], v = node[col];
    if (SCOPE_FIELDS.includes(col) && v && typeof v === "object" && !Array.isArray(v)
        && Object.keys(v).length === 1 && "$eq" in v && typeof v.$eq !== "object") {
      return { scopeField: col, scopeValue: String(v.$eq) };
    }
    return null;
  };
  if (obj && Array.isArray(obj.$and) && obj.$and.length === 2) {
    const s = asScope(obj.$and[0]);
    if (s) return { ...s, conditionJson: JSON.stringify(obj.$and[1]) };
  }
  const whole = asScope(obj);
  if (whole) return { ...whole, conditionJson: "" };
  return { ...none, conditionJson: condJson };
}

function parseIntervalHours(scheduleJson) {
  if (!scheduleJson) return 24;
  try {
    const v = JSON.parse(scheduleJson)?.intervalHours;
    return Number.isInteger(v) && v >= 1 ? v : 24;
  } catch { return 24; }
}

function formatBytes(n) {
  if (!n || n <= 0) return "0 B";
  const u = ["B", "KB", "MB", "GB", "TB"];
  let i = 0, v = n;
  while (v >= 1024 && i < u.length - 1) { v /= 1024; i++; }
  return `${v.toFixed(i === 0 ? 0 : 1)} ${u[i]}`;
}

// Per-database lifecycle (retention) rule management: the compliance guard, the rule
// registry (CRUD), and per-rule Preview / Apply / run-history.
export default function LifecyclePanel({ token, databaseId, databaseName, propertiesJson }) {
  const { t } = useTranslation();
  const [rules, setRules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [info, setInfo] = useState("");

  const [form, setForm] = useState(emptyForm);
  const [editing, setEditing] = useState(false);
  const [showForm, setShowForm] = useState(false);

  // { ruleId, type: "preview"|"apply"|"runs", data } shown below the list.
  const [activeResult, setActiveResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const [openStep, setOpenStep] = useState("when"); // which flow step is expanded
  const toggleStep = (s) => setOpenStep((cur) => (cur === s ? "" : s));

  // Date-typed columns the "Age based on" step may use. DocumentDate is the built-in canonical
  // date column; the rest come from this database's date/datetime indexed fields.
  const [dateFields, setDateFields] = useState(["DocumentDate"]);
  // All field names (system columns + indexed fields) offered as WHERE-condition suggestions.
  const [allFields, setAllFields] = useState(SYSTEM_FIELDS);

  // Move-target pickers: enabled storage locations (Tier target) and sibling databases (Database target).
  const [storageLocations, setStorageLocations] = useState([]);
  const [databases, setDatabases] = useState([]);

  // Compliance guard, seeded once from the database's PropertiesJson (lazy initializer).
  const [guard, setGuard] = useState(() => {
    try {
      const g = JSON.parse(propertiesJson || "{}")?.retentionGuard || {};
      return {
        legalHold: !!g.legalHold,
        minRetentionValue: g.minRetentionValue ?? "",
        minRetentionUnit: g.minRetentionUnit || "Years",
      };
    } catch { return { legalHold: false, minRetentionValue: "", minRetentionUnit: "Years" }; }
  });

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const data = await getLifecycleRules(token, databaseId);
      setRules(Array.isArray(data) ? data : []);
    } catch (e) {
      setError(e?.message || String(e));
    } finally {
      setLoading(false);
    }
  }, [token, databaseId]);

  useEffect(() => { load(); }, [load]);

  // Pull this database's date-typed columns so "Age based on" offers a picker, not free text.
  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const ix = await getDatabaseIndexes(token, databaseId);
        const list = Array.isArray(ix) ? ix : (ix?.indexes ?? ix?.Indexes ?? []);
        const names = list.map((f) => f.fieldName || f.FieldName).filter(Boolean);
        const dates = list
          .filter((f) => ["date", "datetime"].includes((f.dataType || f.DataType || "").toLowerCase()))
          .map((f) => f.fieldName || f.FieldName)
          .filter(Boolean);
        if (active) {
          setDateFields(["DocumentDate", ...dates.filter((d) => d !== "DocumentDate")]);
          setAllFields([...SYSTEM_FIELDS, ...names.filter((n) => !SYSTEM_FIELDS.includes(n))]);
        }
      } catch { /* keep the DocumentDate default if indexes can't be loaded */ }
    })();
    return () => { active = false; };
  }, [token, databaseId]);

  // Load Move-target options once: storage locations (Tier) and the other databases (cross-DB).
  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const [locs, dbs] = await Promise.all([getStorageLocations(token), getDatabases(token)]);
        if (!active) return;
        setStorageLocations(Array.isArray(locs) ? locs : []);
        setDatabases(Array.isArray(dbs) ? dbs : []);
      } catch { /* the Move target pickers just stay empty if these can't be loaded */ }
    })();
    return () => { active = false; };
  }, [token]);

  function resetForm() { setForm(emptyForm); setEditing(false); setShowForm(false); }
  function startAdd() { setForm(emptyForm); setEditing(false); setError(""); setInfo(""); setActiveResult(null); setShowForm(true); }

  function startEdit(r) {
    setEditing(true); setShowForm(true); setInfo(""); setError(""); setActiveResult(null);
    const split = splitCondition(r.conditionJson || "");
    const move = splitMoveParams(r.actionParamsJson || r.ActionParamsJson || "");
    setForm({
      id: r.id, name: r.name || "", description: r.description || "", enabled: r.enabled !== false,
      priority: r.priority ?? 0, dateBasis: r.dateBasis || "DocumentDate",
      maxAgeValue: r.maxAgeValue ?? "", maxAgeUnit: r.maxAgeUnit || "Years",
      action: r.action || "SoftDelete", deletionDepth: r.deletionDepth || "Physical",
      ...move,
      executionMode: r.executionMode || "Manual", intervalHours: parseIntervalHours(r.scheduleJson),
      scopeField: split.scopeField, scopeValue: split.scopeValue, conditionJson: split.conditionJson,
    });
  }

  // Combine the structural scope clause and the free-form WHERE into one FilterExpression.
  function combinedCondition() {
    const sc = (form.scopeField && form.scopeValue.trim())
      ? { [form.scopeField]: { "$eq": form.scopeValue.trim() } } : null;
    let where = null;
    const wt = form.conditionJson?.trim();
    if (wt) { try { where = JSON.parse(wt.replace(/'/g, '"')); } catch { where = null; } }
    const combined = (sc && where) ? { "$and": [sc, where] } : (sc || where || null);
    return combined ? JSON.stringify(combined) : null;
  }

  // Build the {targetType, target id} JSON the engine expects for a Move rule; null for other actions.
  function buildMoveParams() {
    if (form.action !== "Move") return null;
    if (form.moveTargetType === "Database") {
      const id = Number(form.moveTargetDbId);
      return id > 0 ? JSON.stringify({ targetType: "Database", targetDbId: id }) : null;
    }
    const id = Number(form.moveTargetStorageLocationId);
    return id > 0 ? JSON.stringify({ targetType: "Tier", targetStorageLocationId: id }) : null;
  }

  // Human label for the currently-selected Move target (for the collapsed step summary).
  function moveTargetLabel() {
    if (form.moveTargetType === "Database") {
      const d = databases.find((x) => String(dbId(x)) === String(form.moveTargetDbId));
      return d ? dbName(d) : (form.moveTargetDbId ? `#${form.moveTargetDbId}` : "");
    }
    const l = storageLocations.find((x) => String(x.id) === String(form.moveTargetStorageLocationId));
    return l ? l.name : (form.moveTargetStorageLocationId ? `#${form.moveTargetStorageLocationId}` : "");
  }

  function buildPayload() {
    const maxAgeValue = form.maxAgeValue === "" || form.maxAgeValue === null ? null : Number(form.maxAgeValue);
    const isMove = form.action === "Move";
    // Move can't be scheduled yet (Preview/Apply only) — the REST layer rejects a Scheduled Move, so
    // pin Move rules to Manual here and don't emit a schedule.
    const executionMode = isMove ? "Manual" : form.executionMode;
    return {
      ...(editing ? { id: form.id } : { databaseId }),
      name: form.name.trim(),
      description: form.description?.trim() || null,
      enabled: form.enabled,
      priority: Number(form.priority) || 0,
      dateBasis: form.dateBasis?.trim() || "DocumentDate",
      maxAgeValue,
      maxAgeUnit: maxAgeValue ? form.maxAgeUnit : null,
      conditionJson: combinedCondition(),
      action: form.action,
      // DeletionDepth is only meaningful for a Delete action; default Soft for SoftDelete/Move rules.
      deletionDepth: form.action === "Delete" ? form.deletionDepth : "Soft",
      actionParamsJson: buildMoveParams(),
      executionMode,
      scheduleJson: executionMode === "Scheduled"
        ? JSON.stringify({ intervalHours: Math.max(1, Number(form.intervalHours) || 24) })
        : null,
    };
  }

  async function handleSave(e) {
    e.preventDefault();
    setError(""); setInfo("");
    if (!form.name.trim()) { setError(t("lifecycle.required")); return; }
    if (form.conditionJson?.trim()) {
      try { JSON.parse(form.conditionJson.replace(/'/g, '"')); }
      catch { setError(t("lifecycle.conditionInvalid")); return; }
    }
    if (form.action === "Move" && !buildMoveParams()) { setError(t("lifecycle.moveNeedsTarget")); return; }
    try {
      const payload = buildPayload();
      if (editing) await updateLifecycleRule(token, payload);
      else await createLifecycleRule(token, payload);
      resetForm();
      await load();
      setInfo(t("lifecycle.saved"));
    } catch (e) {
      setError(e?.message || String(e));
    }
  }

  async function handleDelete(r) {
    if (!window.confirm(t("lifecycle.deleteConfirm", { name: r.name }))) return;
    setError(""); setInfo("");
    try { await deleteLifecycleRule(token, r.id); await load(); }
    catch (e) { setError(e?.message || String(e)); }
  }

  async function runAction(r, type) {
    setError(""); setInfo(""); setBusy(true); setActiveResult({ ruleId: r.id, type, data: null });
    try {
      let data;
      if (type === "preview") data = await previewLifecycleRule(token, r.id);
      else if (type === "apply") {
        if (!window.confirm(t("lifecycle.applyConfirm", { name: r.name }))) { setActiveResult(null); setBusy(false); return; }
        data = await applyLifecycleRule(token, r.id);
        await load();
      } else if (type === "runs") data = await getLifecycleRuns(token, databaseId, r.id);
      setActiveResult({ ruleId: r.id, type, data });
    } catch (e) {
      setError(e?.message || String(e));
      setActiveResult(null);
    } finally {
      setBusy(false);
    }
  }

  async function handleSaveGuard(e) {
    e.preventDefault();
    setError(""); setInfo("");
    const hasFloor = guard.minRetentionValue !== "" && Number(guard.minRetentionValue) > 0;
    try {
      await setRetentionGuard(token, {
        databaseId,
        legalHold: !!guard.legalHold,
        minRetentionValue: hasFloor ? Number(guard.minRetentionValue) : null,
        minRetentionUnit: hasFloor ? guard.minRetentionUnit : null,
      });
      setInfo(t("lifecycle.guardSaved"));
    } catch (e) {
      setError(e?.message || String(e));
    }
  }

  const unitLabel = (u) => t(`lifecycle.unit${u}`);

  return (
    <div className="lc-panel">
        {error ? <div className="arch-alert arch-alert--error" role="alert">{error}</div> : null}
        {info ? <div className="arch-alert arch-alert--success">{info}</div> : null}

        {/* ── Compliance guard ─────────────────────────────────────────── */}
        <div className="sl-bind-card">
          <div className="dbPropsSectionTitle" style={{ marginTop: 0 }}>{t("lifecycle.guardTitle")}</div>
          <form onSubmit={handleSaveGuard} className="sl-form">
            <label className="sl-check sl-field--full">
              <input type="checkbox" checked={guard.legalHold}
                     onChange={(e) => setGuard({ ...guard, legalHold: e.target.checked })} />
              {t("lifecycle.legalHold")}
            </label>
            <div className="sl-field">
              <label className="createDbLabel">{t("lifecycle.minRetention")}</label>
              <input className="createDbInput" type="number" min="0" value={guard.minRetentionValue}
                     onChange={(e) => setGuard({ ...guard, minRetentionValue: e.target.value })} placeholder={t("lifecycle.none")} />
            </div>
            <div className="sl-field">
              <label className="createDbLabel">&nbsp;</label>
              <select className="createDbInput" value={guard.minRetentionUnit}
                      onChange={(e) => setGuard({ ...guard, minRetentionUnit: e.target.value })}>
                {AGE_UNITS.map((u) => <option key={u} value={u}>{unitLabel(u)}</option>)}
              </select>
            </div>
            <div className="sl-form-actions sl-field--full">
              <button type="submit" className="primary-btn">{t("lifecycle.guardSave")}</button>
            </div>
          </form>
        </div>

        {/* ── Rule registry ────────────────────────────────────────────── */}
        <div className="sl-bind-card">
        <div className="dbPropsSectionTitle" style={{ marginTop: 0 }}>{t("lifecycle.registry")}</div>
        {loading ? (
          <div className="sl-muted">{t("common.loading")}</div>
        ) : rules.length === 0 ? (
          <div className="sl-muted">{t("lifecycle.empty")}</div>
        ) : (
          <div className="sl-list">
            {rules.map((r) => (
              <div key={r.id} className={`sl-row${r.enabled === false ? " sl-row--disabled" : ""}`}>
                <div className="sl-row-main">
                  <span className="sl-row-name">{r.name}</span>
                  <span className="sl-badge">{r.action === "Delete" ? t("lifecycle.actionDelete") : r.action === "Move" ? t("lifecycle.actionMove") : t("lifecycle.actionSoftDelete")}</span>
                  {r.executionMode === "Scheduled" ? <span className="sl-badge sl-badge--tier">{t("lifecycle.modeScheduled")}</span> : null}
                </div>
                <div className="sl-row-path">
                  {r.maxAgeValue ? t("lifecycle.olderThanLabel", { value: r.maxAgeValue, unit: unitLabel(r.maxAgeUnit || "Years") }) : t("lifecycle.noAge")}
                  {r.conditionJson ? " · " + t("lifecycle.hasCondition") : ""}
                </div>
                <div className="sl-row-actions">
                  <button className="link-btn" disabled={busy} onClick={() => runAction(r, "preview")}>{t("lifecycle.preview")}</button>
                  <button className="link-btn" disabled={busy} onClick={() => runAction(r, "apply")}>{t("lifecycle.apply")}</button>
                  <button className="link-btn" disabled={busy} onClick={() => runAction(r, "runs")}>{t("lifecycle.runs")}</button>
                  <button className="link-btn" onClick={() => startEdit(r)}>{t("lifecycle.edit")}</button>
                  <button className="link-btn sl-danger" onClick={() => handleDelete(r)}>{t("common.remove")}</button>
                </div>
                {activeResult && activeResult.ruleId === r.id ? (
                  <div className="sl-field--full" style={{ marginTop: 8 }}>
                    {renderResult(activeResult, t)}
                  </div>
                ) : null}
              </div>
            ))}
          </div>
        )}

        {!showForm ? (
          <div className="sl-form-actions" style={{ marginTop: 16 }}>
            <button type="button" className="primary-btn" onClick={startAdd}>+ {t("lifecycle.addTitle")}</button>
          </div>
        ) : (
          <>
            <div className="dbPropsSectionTitle">{editing ? t("lifecycle.editTitle") : t("lifecycle.addTitle")}</div>
            <form onSubmit={handleSave} className="lc-flow">
              <input className="lc-flow-name" value={form.name} placeholder={t("lifecycle.name")}
                     onChange={(e) => setForm({ ...form, name: e.target.value })} required />
              <input className="lc-flow-desc" value={form.description} placeholder={t("lifecycle.description")}
                     onChange={(e) => setForm({ ...form, description: e.target.value })} />

              {/* Scope — narrow by a structural dimension within this database */}
              <FlowStep icon="📁" label={t("lifecycle.stepScope")} open={openStep === "scope"} onToggle={() => toggleStep("scope")}
                summary={form.scopeField && form.scopeValue.trim()
                  ? `${t("lifecycle." + SCOPE_FIELD_KEY[form.scopeField])} = ${form.scopeValue.trim()}`
                  : t("lifecycle.scopeSummary", { db: databaseName })}>
                <div className="sl-hint">{t("lifecycle.scopeSummary", { db: databaseName })}</div>
                <label className="createDbLabel" style={{ marginTop: 4 }}>{t("lifecycle.scopeLimitTo")}</label>
                <div className="lc-inline">
                  <select className="createDbInput" value={form.scopeField}
                          onChange={(e) => setForm({ ...form, scopeField: e.target.value })}>
                    <option value="">{t("lifecycle.scopeWhole")}</option>
                    {SCOPE_FIELDS.map((f) => <option key={f} value={f}>{t("lifecycle." + SCOPE_FIELD_KEY[f])}</option>)}
                  </select>
                  {form.scopeField ? (
                    <input className="createDbInput" value={form.scopeValue} placeholder={t("lifecycle.scopeValue")}
                           onChange={(e) => setForm({ ...form, scopeValue: e.target.value })} />
                  ) : null}
                </div>
              </FlowStep>

              {/* When — age */}
              <FlowStep icon="⏱" label={t("lifecycle.stepWhen")} open={openStep === "when"} onToggle={() => toggleStep("when")}
                summary={form.maxAgeValue
                  ? `${t("lifecycle.olderThanLabel", { value: form.maxAgeValue, unit: unitLabel(form.maxAgeUnit) })} · ${form.dateBasis || "DocumentDate"}`
                  : t("lifecycle.noAge")}>
                <div className="lc-inline">
                  <input className="createDbInput lc-w-sm" type="number" min="1" value={form.maxAgeValue}
                         onChange={(e) => setForm({ ...form, maxAgeValue: e.target.value })} placeholder={t("lifecycle.none")} />
                  <select className="createDbInput lc-w-sm" value={form.maxAgeUnit} onChange={(e) => setForm({ ...form, maxAgeUnit: e.target.value })}>
                    {AGE_UNITS.map((u) => <option key={u} value={u}>{unitLabel(u)}</option>)}
                  </select>
                </div>
                <label className="createDbLabel" style={{ marginTop: 8 }}>{t("lifecycle.dateBasis")}</label>
                <select className="createDbInput" value={form.dateBasis || "DocumentDate"}
                        onChange={(e) => setForm({ ...form, dateBasis: e.target.value })}>
                  {(dateFields.includes(form.dateBasis) || !form.dateBasis ? dateFields : [form.dateBasis, ...dateFields])
                    .map((f) => <option key={f} value={f}>{f}</option>)}
                </select>
                <div className="sl-hint">{t("lifecycle.whenHint")}</div>
              </FlowStep>

              {/* Where — condition */}
              <FlowStep icon="◇" label={t("lifecycle.stepWhere")} open={openStep === "where"} onToggle={() => toggleStep("where")}
                summary={whereSummary(form.conditionJson, t)}>
                <WhereBuilder key={editing ? `edit-${form.id}` : "add"} value={form.conditionJson} fieldNames={allFields} t={t}
                              onChange={(v) => setForm((f) => ({ ...f, conditionJson: v }))} />
              </FlowStep>

              {/* Then — action */}
              <FlowStep icon="⚡" label={t("lifecycle.stepThen")} open={openStep === "then"} onToggle={() => toggleStep("then")}
                summary={form.action === "Delete"
                  ? t(form.deletionDepth === "Physical" ? "lifecycle.depthPhysical" : "lifecycle.depthSoft")
                  : form.action === "Move"
                    ? `${t("lifecycle.actionMove")}${moveTargetLabel() ? " → " + moveTargetLabel() : ""}`
                    : t("lifecycle.actionSoftDelete")}>
                <label className={`lc-opt${form.action === "SoftDelete" ? " lc-opt--on" : ""}`}>
                  <input type="radio" name="lc-action" checked={form.action === "SoftDelete"} onChange={() => setForm({ ...form, action: "SoftDelete" })} />
                  <span><b>{t("lifecycle.actionSoftDelete")}</b><span className="lc-opt-note">{t("lifecycle.actionHideNote")}</span></span>
                </label>
                <label className={`lc-opt${form.action === "Delete" ? " lc-opt--on" : ""}`}>
                  <input type="radio" name="lc-action" checked={form.action === "Delete"} onChange={() => setForm({ ...form, action: "Delete" })} />
                  <span><b>{t("lifecycle.actionDelete")}</b><span className="lc-opt-note">{t("lifecycle.actionDeleteNote")}</span></span>
                </label>
                {/* Move forces Manual execution (Scheduled Move is rejected by the API until execution is GA). */}
                <label className={`lc-opt${form.action === "Move" ? " lc-opt--on" : ""}`}>
                  <input type="radio" name="lc-action" checked={form.action === "Move"}
                         onChange={() => setForm({ ...form, action: "Move", executionMode: "Manual" })} />
                  <span><b>{t("lifecycle.actionMove")}</b><span className="lc-opt-note">{t("lifecycle.actionMoveNote")}</span></span>
                </label>
                {form.action === "Delete" ? (
                  <div className="lc-substep">
                    <label className="createDbLabel">{t("lifecycle.depth")}</label>
                    <select className="createDbInput" value={form.deletionDepth} onChange={(e) => setForm({ ...form, deletionDepth: e.target.value })}>
                      <option value="Physical">{t("lifecycle.depthPhysical")}</option>
                      <option value="Soft">{t("lifecycle.depthSoft")}</option>
                    </select>
                  </div>
                ) : null}
                {form.action === "Move" ? (
                  <div className="lc-substep">
                    <label className="createDbLabel">{t("lifecycle.moveTargetType")}</label>
                    <select className="createDbInput" value={form.moveTargetType}
                            onChange={(e) => setForm({ ...form, moveTargetType: e.target.value })}>
                      <option value="Tier">{t("lifecycle.moveTargetTier")}</option>
                      <option value="Database">{t("lifecycle.moveTargetDatabase")}</option>
                    </select>
                    {form.moveTargetType === "Database" ? (
                      <>
                        <label className="createDbLabel" style={{ marginTop: 8 }}>{t("lifecycle.movePickDatabase")}</label>
                        <select className="createDbInput" value={form.moveTargetDbId}
                                onChange={(e) => setForm({ ...form, moveTargetDbId: e.target.value })}>
                          <option value="">{t("lifecycle.movePick")}</option>
                          {databases.filter((d) => String(dbId(d)) !== String(databaseId)).map((d) => (
                            <option key={dbId(d)} value={dbId(d)}>{dbName(d)}</option>
                          ))}
                        </select>
                      </>
                    ) : (
                      <>
                        <label className="createDbLabel" style={{ marginTop: 8 }}>{t("lifecycle.movePickTier")}</label>
                        <select className="createDbInput" value={form.moveTargetStorageLocationId}
                                onChange={(e) => setForm({ ...form, moveTargetStorageLocationId: e.target.value })}>
                          <option value="">{t("lifecycle.movePick")}</option>
                          {storageLocations.filter((l) => l.enabled).map((l) => (
                            <option key={l.id} value={l.id}>{l.name} ({l.kind}{l.tier ? " · " + l.tier : ""})</option>
                          ))}
                        </select>
                      </>
                    )}
                    <div className="sl-hint">{t("lifecycle.moveManualOnly")}</div>
                  </div>
                ) : null}
              </FlowStep>

              {/* Run — schedule */}
              <FlowStep icon="▶" label={t("lifecycle.stepRun")} open={openStep === "run"} last
                onToggle={() => toggleStep("run")}
                summary={form.executionMode === "Scheduled" ? t("lifecycle.runEvery", { hours: form.intervalHours }) : t("lifecycle.modeManual")}>
                <label className={`lc-opt${form.executionMode === "Manual" ? " lc-opt--on" : ""}`}>
                  <input type="radio" name="lc-mode" checked={form.executionMode === "Manual"} onChange={() => setForm({ ...form, executionMode: "Manual" })} />
                  <span><b>{t("lifecycle.modeManual")}</b><span className="lc-opt-note">{t("lifecycle.modeManualNote")}</span></span>
                </label>
                <label className={`lc-opt${form.executionMode === "Scheduled" ? " lc-opt--on" : ""}${form.action === "Move" ? " lc-opt--disabled" : ""}`}>
                  <input type="radio" name="lc-mode" disabled={form.action === "Move"}
                         checked={form.executionMode === "Scheduled"} onChange={() => setForm({ ...form, executionMode: "Scheduled" })} />
                  <span><b>{t("lifecycle.modeScheduled")}</b><span className="lc-opt-note">{form.action === "Move" ? t("lifecycle.moveManualOnly") : t("lifecycle.modeScheduledNote")}</span></span>
                </label>
                {form.executionMode === "Scheduled" && form.action !== "Move" ? (
                  <div className="lc-substep lc-inline">
                    <label className="createDbLabel" style={{ margin: 0 }}>{t("lifecycle.intervalHours")}</label>
                    <input className="createDbInput lc-w-sm" type="number" min="1" value={form.intervalHours}
                           onChange={(e) => setForm({ ...form, intervalHours: e.target.value })} />
                  </div>
                ) : null}
                <div className="lc-substep lc-inline">
                  <label className="createDbLabel" style={{ margin: 0 }}>{t("lifecycle.priority")}</label>
                  <input className="createDbInput lc-w-sm" type="number" value={form.priority}
                         onChange={(e) => setForm({ ...form, priority: e.target.value })} />
                </div>
              </FlowStep>

              {/* Compliance guard band (reference) */}
              <div className="lc-guard-band">
                <span>🛡</span>
                <span>{guard.legalHold
                  ? t("lifecycle.guardHold")
                  : (guard.minRetentionValue && Number(guard.minRetentionValue) > 0
                      ? t("lifecycle.guardKeep", { value: guard.minRetentionValue, unit: unitLabel(guard.minRetentionUnit) })
                      : t("lifecycle.guardNone"))}</span>
              </div>

              <label className="sl-check sl-field--full">
                <input type="checkbox" checked={form.enabled} onChange={(e) => setForm({ ...form, enabled: e.target.checked })} />
                {t("lifecycle.enabled")}
              </label>

              <div className="sl-form-actions sl-field--full">
                <button type="submit" className="primary-btn">{editing ? t("common.save") : t("lifecycle.add")}</button>
                <button type="button" className="dbModalCancel" onClick={resetForm}>{t("common.cancel")}</button>
              </div>
            </form>
          </>
        )}
        </div>
    </div>
  );
}

// One step in the vertical flow builder: a connected, click-to-configure node card.
function FlowStep({ icon, label, summary, open, onToggle, children, last }) {
  return (
    <div className={`lc-node${last ? " lc-node--last" : ""}`}>
      <button type="button" className={`lc-step${open ? " lc-step--open" : ""}`} onClick={onToggle}>
        <span className="lc-step-icon">{icon}</span>
        <span className="lc-step-text">
          <span className="lc-step-label">{label}</span>
          <span className="lc-step-sum">{summary}</span>
        </span>
        <span className="lc-step-chev">{open ? "▾" : "▸"}</span>
      </button>
      {open ? <div className="lc-step-body">{children}</div> : null}
    </div>
  );
}

// ── WHERE condition builder ──────────────────────────────────────────────────
// A visual row-per-condition editor that compiles to the FilterExpression DSL the engine
// consumes. Power users can flip to a raw-JSON editor for shapes the builder can't express
// (nested groups, mixed AND/OR). The canonical value stays the JSON string in form.conditionJson.

// One clause { Field: { $op: value } } → a builder row, or null if not a simple clause.
function clauseToRow(node) {
  if (!node || typeof node !== "object" || Array.isArray(node)) return null;
  const fields = Object.keys(node);
  if (fields.length !== 1 || fields[0].startsWith("$")) return null;
  const field = fields[0];
  const opObj = node[field];
  if (!opObj || typeof opObj !== "object" || Array.isArray(opObj)) return null;
  const ops = Object.keys(opObj);
  if (ops.length !== 1 || !WHERE_OPS.includes(ops[0])) return null;
  const op = ops[0], v = opObj[op];
  if (op === "$between") {
    if (!Array.isArray(v) || v.length !== 2) return null;
    return { field, op, value: String(v[0] ?? ""), value2: String(v[1] ?? "") };
  }
  if (v !== null && typeof v === "object") return null;
  return { field, op, value: String(v ?? ""), value2: "" };
}

// Decompose a stored ConditionJson into { mode, combinator, rows, raw }. Falls back to raw mode
// whenever the JSON isn't a flat AND/OR (or single clause) of simple field comparisons.
function parseWhere(jsonStr) {
  const raw = jsonStr || "";
  if (!raw.trim()) return { mode: "builder", combinator: "$and", rows: [], raw: "" };
  let obj;
  try { obj = JSON.parse(raw.replace(/'/g, '"')); } catch { return { mode: "raw", combinator: "$and", rows: [], raw }; }
  if (!obj || typeof obj !== "object" || Array.isArray(obj)) return { mode: "raw", combinator: "$and", rows: [], raw };
  const keys = Object.keys(obj);
  let combinator = "$and", arr;
  if (keys.length === 1 && (keys[0] === "$and" || keys[0] === "$or")) {
    combinator = keys[0];
    arr = obj[keys[0]];
    if (!Array.isArray(arr)) return { mode: "raw", combinator: "$and", rows: [], raw };
  } else {
    arr = [obj]; // a single bare clause
  }
  const rows = [];
  for (const el of arr) {
    const r = clauseToRow(el);
    if (!r) return { mode: "raw", combinator: "$and", rows: [], raw };
    rows.push(r);
  }
  return { mode: "builder", combinator, rows, raw };
}

// Builder rows → ConditionJson string ("" when no complete rows).
function buildWhere(combinator, rows) {
  const clauses = rows
    .filter((r) => r.field.trim() && (r.op === "$between" ? (r.value !== "" && r.value2 !== "") : r.value !== ""))
    .map((r) => ({
      [r.field.trim()]: { [r.op]: r.op === "$between" ? [r.value, r.value2] : r.value },
    }));
  if (clauses.length === 0) return "";
  return JSON.stringify({ [combinator]: clauses });
}

// Collapsed-step summary for the WHERE node.
function whereSummary(jsonStr, t) {
  if (!jsonStr || !jsonStr.trim()) return t("lifecycle.whereAll");
  const p = parseWhere(jsonStr);
  if (p.mode === "raw" || p.rows.length === 0) return t("lifecycle.hasCondition");
  // A single condition reads as "Field op value"; multiples collapse to a count.
  if (p.rows.length === 1) {
    const r = p.rows[0];
    const val = r.op === "$between" ? `${r.value}–${r.value2}` : r.value;
    return `${r.field} ${t("lifecycle.op_" + r.op.slice(1))} ${val}`.trim();
  }
  return t("lifecycle.whereCount", { count: p.rows.length });
}

function WhereBuilder({ value, onChange, fieldNames, t }) {
  const init = useMemo(() => parseWhere(value), []); // parse once; remounted (via key) when the rule changes
  const [mode, setMode] = useState(init.mode);
  const [combinator, setCombinator] = useState(init.combinator);
  const [rows, setRows] = useState(init.rows);
  const [raw, setRaw] = useState(init.raw);
  const [note, setNote] = useState("");
  const listId = "lc-where-fields";

  const push = (cb, rws) => onChange(buildWhere(cb, rws));
  const setCombi = (c) => { setCombinator(c); push(c, rows); };
  const updateRow = (i, patch) => { const next = rows.map((r, idx) => (idx === i ? { ...r, ...patch } : r)); setRows(next); push(combinator, next); };
  const addRow = () => { const next = [...rows, { field: "", op: "$eq", value: "", value2: "" }]; setRows(next); push(combinator, next); };
  const removeRow = (i) => { const next = rows.filter((_, idx) => idx !== i); setRows(next); push(combinator, next); };

  const toRaw = () => { setRaw(buildWhere(combinator, rows)); setNote(""); setMode("raw"); };
  const toBuilder = () => {
    const p = parseWhere(raw);
    if (p.mode === "raw") { setNote(t("lifecycle.whereRawOnly")); return; }
    setCombinator(p.combinator); setRows(p.rows); setNote(""); setMode("builder");
    onChange(buildWhere(p.combinator, p.rows));
  };

  if (mode === "raw") {
    return (
      <>
        <textarea className="createDbInput" rows={4} style={{ fontFamily: "monospace", fontSize: 12 }}
                  value={raw} onChange={(e) => { setRaw(e.target.value); onChange(e.target.value); }}
                  placeholder={'{"$and":[{"Status":{"$eq":"CLOSED"}}]}'} />
        <div className="sl-hint">{t("lifecycle.conditionHint")}</div>
        {note ? <div className="sl-hint" style={{ color: "#b91c1c" }}>{note}</div> : null}
        <button type="button" className="link-btn" onClick={toBuilder}>{t("lifecycle.whereUseBuilder")}</button>
      </>
    );
  }

  return (
    <>
      <datalist id={listId}>{fieldNames.map((f) => <option key={f} value={f} />)}</datalist>
      {rows.length > 1 ? (
        <div className="lc-inline" style={{ marginBottom: 6 }}>
          <label className={`lc-opt lc-opt--inline${combinator === "$and" ? " lc-opt--on" : ""}`}>
            <input type="radio" checked={combinator === "$and"} onChange={() => setCombi("$and")} /> {t("lifecycle.whereMatchAll")}
          </label>
          <label className={`lc-opt lc-opt--inline${combinator === "$or" ? " lc-opt--on" : ""}`}>
            <input type="radio" checked={combinator === "$or"} onChange={() => setCombi("$or")} /> {t("lifecycle.whereMatchAny")}
          </label>
        </div>
      ) : null}

      {rows.length === 0 ? <div className="sl-hint">{t("lifecycle.whereNone")}</div> : null}

      {rows.map((r, i) => (
        <div key={i} className="lc-cond-row">
          <input className="createDbInput lc-cond-field" list={listId} value={r.field} placeholder={t("lifecycle.whereField")}
                 onChange={(e) => updateRow(i, { field: e.target.value })} />
          <select className="createDbInput lc-cond-op" value={r.op} onChange={(e) => updateRow(i, { op: e.target.value })}>
            {WHERE_OPS.map((op) => <option key={op} value={op}>{t("lifecycle.op_" + op.slice(1))}</option>)}
          </select>
          <input className="createDbInput lc-cond-val" value={r.value} placeholder={t("lifecycle.whereValue")}
                 onChange={(e) => updateRow(i, { value: e.target.value })} />
          {r.op === "$between" ? (
            <>
              <span className="lc-cond-and">{t("lifecycle.whereAnd")}</span>
              <input className="createDbInput lc-cond-val" value={r.value2} placeholder={t("lifecycle.whereValue")}
                     onChange={(e) => updateRow(i, { value2: e.target.value })} />
            </>
          ) : null}
          <button type="button" className="link-btn sl-danger lc-cond-x" title={t("common.remove")} onClick={() => removeRow(i)}>✕</button>
        </div>
      ))}

      <div className="lc-inline" style={{ marginTop: 4 }}>
        <button type="button" className="link-btn" onClick={addRow}>+ {t("lifecycle.whereAdd")}</button>
        <button type="button" className="link-btn" onClick={toRaw} style={{ marginLeft: "auto" }}>{t("lifecycle.whereAdvanced")}</button>
      </div>
    </>
  );
}

function renderResult(active, t) {
  const { type, data } = active;
  if (!data) return <div className="sl-muted">{t("common.loading")}</div>;

  if (type === "runs") {
    const runs = Array.isArray(data) ? data : [];
    if (runs.length === 0) return <div className="sl-muted">{t("lifecycle.runsEmpty")}</div>;
    return (
      <div className="lc-result">
        <div className="dbPropsSectionTitle" style={{ marginTop: 0 }}>{t("lifecycle.runsTitle")}</div>
        {runs.map((run) => (
          <div key={run.id} className="lc-run">
            <span className={`sl-badge lc-status-${(run.status || "").toLowerCase()}`}>{run.status}</span>
            <span className="lc-run-meta">{(run.runAtUtc || "").replace("T", " ").slice(0, 19)} · {run.trigger}</span>
            <span className="lc-run-meta">{t("lifecycle.resultReclaimed")}: {run.docsActioned ?? 0} · {formatBytes(run.bytesReclaimed)}</span>
          </div>
        ))}
      </div>
    );
  }

  if (data.blocked) {
    return <div className="arch-alert arch-alert--error" style={{ margin: 0 }}>{t("lifecycle.resultBlocked")}</div>;
  }

  const rows = type === "preview"
    ? [
        [t("lifecycle.resultMatched"), data.docsMatched],
        [t("lifecycle.resultReclaimable"), `${data.docsReclaimable} (${formatBytes(data.reclaimableBytes)})`],
        [t("lifecycle.resultChunks"), `${data.chunksMatched} / ${data.chunksDeferred} ${t("lifecycle.resultDeferred")}`],
        [t("lifecycle.resultStrategy"), data.strategy],
      ]
    : [
        [t("lifecycle.resultReclaimed"), `${data.docsReclaimed} (${formatBytes(data.bytesReclaimed)})`],
        [t("lifecycle.resultHidden"), data.docsHidden],
        [t("lifecycle.resultStrategy"), data.strategy],
        [t("lifecycle.status"), data.status],
      ];

  return (
    <div className="lc-result">
      <div className="lc-result-grid">
        {rows.map(([k, v]) => (
          <div key={k} className="lc-result-cell"><span className="lc-result-k">{k}</span><span className="lc-result-v">{v}</span></div>
        ))}
      </div>
      {data.cutoffSummary ? <div className="sl-hint">{data.cutoffSummary}</div> : null}
      {data.error ? <div className="arch-alert arch-alert--error" style={{ margin: "6px 0 0" }}>{data.error}</div> : null}
    </div>
  );
}
